import Papa from 'papaparse';
import { supabase } from '../configs/supabase';
import dayjs from 'dayjs';

export const handleFileUpload = async (
  event: React.ChangeEvent<HTMLInputElement>,
  tableName: string,
  fetchFunction: () => void
): Promise<string> => {
  const file = event.target.files?.[0];

  if (!file) return "ファイルが選択されていません。";

  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: "Shift-JIS",
      complete: async (results) => {
        const { data: csvData, errors } = results;
        if (errors.length) {
          console.error("CSVパースエラー:", errors);
          resetFileInput(event.target);
          resolve("CSVパースエラーが発生しました。");
          return;
        }

        // 現在のデータベースのデータを取得
        const { data: dbData, error: selectError } = await supabase
          .from(tableName)
          .select('id')
          .order('id', { ascending: false });
        
        if (selectError) {
          console.error("データの取得失敗:", selectError);
          resetFileInput(event.target);
          resolve("データの取得に失敗しました。");
          return;
        }

        const csvIds = csvData.map((item: any) => Number(item.id));
        const dbIds = dbData?.map((item: any) => item.id) || [];

        // 削除対象のIDを判定
        const idsToDelete = dbIds.filter((id: number) => !csvIds.includes(id));

        // 削除対象のデータを削除(後ろからデータを確認)
        // supabaseの制限で一度に削除できるデータは10000件まで
        if (idsToDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from(tableName)
            .delete()
            .in('id', idsToDelete);

          if (deleteError) {
            console.error("データの削除失敗:", deleteError);
            resetFileInput(event.target);
            resolve("データの削除に失敗しました。" + deleteError.message + " " + deleteError.details);
            return;
          }
        }

        // 現在の日時を取得
        const currentDate = dayjs().tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss');

        // csvDataにupdated_atフィールドを追加
        const updatedCsvData = csvData.map((item: any) => ({
          ...item,
          updated_at: currentDate
        }));

        const { error } = await supabase.from(tableName).upsert(updatedCsvData, { onConflict: 'id' });
        if (error) {
          console.error("データのインポート失敗:", error);
          resetFileInput(event.target);
          // 問い合わせがきたときにわかるようにエラーの詳細内容を表示
          resolve("データのインポートに失敗しました。" + error.message + " " + error.details);
        } else {
          console.log("データのインポート成功:", csvData);
          resetFileInput(event.target);
          fetchFunction();
          resolve("正常にインポートしました。");
        }
      }
    });
  });
};

export const handleMultipleFileUpload = async (
  event: React.ChangeEvent<HTMLInputElement>,
): Promise<string> => {
  const files = event.target.files;

  if (!files || files.length !== 4) {
    return "アップロードするファイルが足りません。";
  }

  const expectedTables = ['customerProduct', 'customerShip', 'customer', 'product'];
  const fileArray = Array.from(files);
  const fileNames = fileArray.map(file => file.name);
  const timestamps = new Set<string>();

  for (const fileName of fileNames) {
    const parts = fileName.split('_');
    if (parts.length < 3) {
      return `${fileName}のファイル名が正しくありません。`;
    }

    const tableName = parts[1];
    const timestampPart = parts[2].split('.')[0];
    const timestamp = timestampPart.substring(0, 8);

    if (!expectedTables.includes(tableName)) {
      return `${fileName}のファイル名が正しくありません。`;
    }

    timestamps.add(timestamp);
  }

  if (timestamps.size !== 1) {
    return "タイムスタンプが一致するファイルをアップロードしてください。";
  }

  // ファイル名のプレフィックスを環境によって変更
  let filePrefix = ""
  if (process.env.REACT_APP_ENVIRONMENT === 'NAKANO') {
    filePrefix = "0414";
  } else {
    filePrefix = "0004";
  }

  const fixedFiles = [
    fileArray.find(file => new RegExp(`^${filePrefix}_customer_\\d{8}`).test(file.name)),
    fileArray.find(file => new RegExp(`^${filePrefix}_product_\\d{8}`).test(file.name)),
    fileArray.find(file => new RegExp(`^${filePrefix}_customerProduct_\\d{8}`).test(file.name)),
    fileArray.find(file => new RegExp(`^${filePrefix}_customerShip_\\d{8}`).test(file.name)),
  ].filter((file): file is File => file !== undefined);

  if (fixedFiles.length !== 4) {
    return "アップロードするファイルの種類が正しくありません。";
  }

  try {

    // 親テーブルのデータをインポート
    await Promise.all(fixedFiles.slice(0, 2).map(file => importCSV(file, event, true)));

    // 子テーブルのデータをインポート
    await Promise.all(fixedFiles.slice(2).map(file => importCSV(file, event, false)));

    resetFileInput(event.target);
    return "すべてのファイルが正常にインポートされました。";
  } catch (error) {
    return error as string;
  }
};

// 親テーブルのインポート
// csvのidとsupabaseのDBのidを比較して論理削除
const importCSV = async (file: File, event: React.ChangeEvent<HTMLInputElement>, isParent: boolean) => {
  return new Promise<void>((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: "Shift-JIS",
      complete: async (results) => {
        const { data: csvData, errors } = results;
        if (errors.length) {
          console.error("CSVパースエラー:", errors);
          resetFileInput(event.target);
          reject("CSVパースエラーが発生しました。");
          return;
        }

        fetchDataAndDelete(file, event, isParent);
        const currentDate = dayjs().tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss');

        const updatedCsvData = csvData.map((item: any) => ({
          ...item,
          updated_at: currentDate
        }));

        const tableName = file.name.split('_')[1];
        const { error } = await supabase.from(tableName).upsert(updatedCsvData, { onConflict: 'id' });
        if (error) {
          console.error("データのインポート失敗:", error);
          resetFileInput(event.target);
          reject("データのインポートに失敗しました。" + error.message + " " + error.details);
          return;
        } else {
          resolve();
        }
      }
    });
  });
};

// supabaseとcsvのidでcsvに存在しないidを削除
const fetchDataAndDelete = async (
  file: File, 
  event: React.ChangeEvent<HTMLInputElement>, 
  isLogicalDelete: boolean // 論理削除か物理削除か
) => {
  return new Promise<void>((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: "Shift-JIS",
      complete: async (results) => {
        const { data: csvData, errors } = results;
        if (errors.length) {
          console.error("CSVパースエラー:", errors);
          resetFileInput(event.target);
          reject("CSVパースエラーが発生しました。");
          return;
        }

        const tableName = file.name.split('_')[1];

        const { data: dbData, error: selectError } = await supabase
          .from(tableName)
          .select('id')
          .order('id', { ascending: false });

        if (selectError) {
          console.error("データの取得失敗:", selectError);
          resetFileInput(event.target);
          reject("データの取得に失敗しました。");
          return;
        }

        const csvIds = csvData.map((item: any) => Number(item.id));
        const dbIds = dbData?.map((item: any) => item.id) || [];

        const idsToDelete = dbIds.filter((id: number) => !csvIds.includes(id));

        if (idsToDelete.length > 0) {
          const currentDate = dayjs().tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss');
          let deleteError;
          if (isLogicalDelete) {
            ({ error: deleteError } = await supabase
              .from(tableName)
              .update({ deleted_at: currentDate })
              .in('id', idsToDelete));
          } else {
            ({ error: deleteError } = await supabase
              .from(tableName)
              .delete()
              .in('id', idsToDelete));
          }

          if (deleteError) {
            console.error("データの削除失敗:", deleteError);
            resetFileInput(event.target);
            reject("データの削除に失敗しました。" + deleteError.message + " " + deleteError.details);
            return;
          }
        }

        resolve();
      }
    });
  });
};


const resetFileInput = (input: HTMLInputElement) => {
  input.value = '';
};

export const handleExport = async (tableName: string) => {
  const { data, error } = await supabase.from(tableName).select('*');
  if (error) {
    console.error("データのエクスポート失敗:", error);
    return;
  }

  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', 'export.csv');
  link.click();
};

export const formatDate = (dateString: string | undefined) => {
  if (dateString) return dateString.replace('T', ' ');
  return '';
};
